import React from 'react';
import PropTypes from 'prop-types';
import { ARRAY_ITEM_TYPES, FORMATS, THEN_TYPES } from './operators';

const STRING_LIKE = new Set(['string', 'date', 'date-time']);
const NUMERIC = new Set(['integer', 'number']);

/**
 * Per-field constraint editor — renders only the inputs that make sense for
 * the currently selected `type`. Drives both THEN and ELSE branches via the
 * `branch` prop (used only in test-ids / labels).
 *
 * Errors are shaped as `{ [constraintKey]: string[] }` (per validateRules).
 * Each input gets a deterministic `id` + matching `<label htmlFor>`, and
 * `aria-describedby` points at the inline error span when present.
 */
export default function FieldConstraintForm({
	field,
	branch,
	ruleIndex,
	errors,
	onChange,
}) {
	const fieldErrors = errors || {};
	const handleChange = (key, value) => onChange({ ...field, [key]: value });
	const idBase = `xallof-rule-${ruleIndex}-${branch}-${field.fieldName}`;
	const isString = STRING_LIKE.has(field.type);
	const isNumeric = NUMERIC.has(field.type);
	const isArray = field.type === 'array';

	const renderError = (key) => {
		const msgs = fieldErrors[key];
		if (!msgs || msgs.length === 0) return null;
		return (
			<span className="dsv-xallof-builder__error" id={`${idBase}-${key}-err`}>
				{msgs.join(' ')}
			</span>
		);
	};

	const describedBy = (key) => (fieldErrors[key] ? `${idBase}-${key}-err` : undefined);

	return (
		<div className="dsv-xallof-builder__field-constraints">
			<div className="dsv-xallof-builder__field-head">
				<span className="dsv-xallof-builder__field-name">{field.fieldName}</span>
				<label className="dsv-xallof-builder__inline-label">
					<input
						type="checkbox"
						checked={!!field.required}
						onChange={(e) => handleChange('required', e.target.checked)}
						data-testid={`${idBase}-required`}
					/>
					required
				</label>
				<label className="dsv-xallof-builder__inline-label" htmlFor={`${idBase}-type`}>
					type
				</label>
				<select
					id={`${idBase}-type`}
					className="dsv-modal__select"
					value={field.type || 'string'}
					onChange={(e) => handleChange('type', e.target.value)}
					data-testid={`${idBase}-type`}
				>
					{THEN_TYPES.map((t) => (
						<option key={t.value} value={t.value}>{t.label}</option>
					))}
				</select>
			</div>

			<div className="dsv-xallof-builder__constraint-grid">
				{isArray && (
					<>
						<div className="dsv-xallof-builder__field-cell">
							<label htmlFor={`${idBase}-array-item-type`}>array item type</label>
							<select
								id={`${idBase}-array-item-type`}
								className="dsv-modal__select"
								value={field.arrayItemType || ''}
								onChange={(e) => handleChange('arrayItemType', e.target.value || undefined)}
								aria-describedby={describedBy('arrayItemType')}
								data-testid={`${idBase}-array-item-type`}
							>
								<option value="">(select)</option>
								{ARRAY_ITEM_TYPES.map((t) => (
									<option key={t.value} value={t.value}>{t.label}</option>
								))}
							</select>
							{renderError('arrayItemType')}
						</div>
						<div className="dsv-xallof-builder__field-cell">
							<label htmlFor={`${idBase}-contains-const`}>contains</label>
							<input
								id={`${idBase}-contains-const`}
								className="dsv-modal__input"
								type="text"
								value={field.containsConst ?? ''}
								onChange={(e) => handleChange('containsConst', e.target.value)}
								placeholder="scalar value"
								data-testid={`${idBase}-contains-const`}
							/>
						</div>
					</>
				)}

				{isString && (
					<>
						<div className="dsv-xallof-builder__field-cell">
							<label htmlFor={`${idBase}-min-length`}>minLen</label>
							<input
								id={`${idBase}-min-length`}
								className="dsv-modal__input"
								type="number"
								min="0"
								value={field.minLength ?? ''}
								onChange={(e) => handleChange('minLength', e.target.value)}
								aria-describedby={describedBy('minLength')}
								data-testid={`${idBase}-min-length`}
							/>
							{renderError('minLength')}
						</div>
						<div className="dsv-xallof-builder__field-cell">
							<label htmlFor={`${idBase}-max-length`}>maxLen</label>
							<input
								id={`${idBase}-max-length`}
								className="dsv-modal__input"
								type="number"
								min="0"
								value={field.maxLength ?? ''}
								onChange={(e) => handleChange('maxLength', e.target.value)}
								data-testid={`${idBase}-max-length`}
							/>
						</div>
						<div className="dsv-xallof-builder__field-cell">
							<label htmlFor={`${idBase}-pattern`}>pattern</label>
							<input
								id={`${idBase}-pattern`}
								className="dsv-modal__input"
								type="text"
								value={field.pattern ?? ''}
								onChange={(e) => handleChange('pattern', e.target.value)}
								placeholder="regex"
								aria-describedby={describedBy('pattern')}
								data-testid={`${idBase}-pattern`}
							/>
							{renderError('pattern')}
						</div>
						<div className="dsv-xallof-builder__field-cell">
							<label htmlFor={`${idBase}-format`}>format</label>
							<select
								id={`${idBase}-format`}
								className="dsv-modal__select"
								value={field.format ?? ''}
								onChange={(e) => handleChange('format', e.target.value || undefined)}
								data-testid={`${idBase}-format`}
							>
								{FORMATS.map((f) => (
									<option key={f.value} value={f.value}>{f.label}</option>
								))}
							</select>
						</div>
					</>
				)}

				{isNumeric && (
					<>
						<div className="dsv-xallof-builder__field-cell">
							<label htmlFor={`${idBase}-minimum`}>min</label>
							<input
								id={`${idBase}-minimum`}
								className="dsv-modal__input"
								type="number"
								value={field.minimum ?? ''}
								onChange={(e) => handleChange('minimum', e.target.value)}
								aria-describedby={describedBy('minimum')}
								data-testid={`${idBase}-minimum`}
							/>
							{renderError('minimum')}
						</div>
						<div className="dsv-xallof-builder__field-cell">
							<label htmlFor={`${idBase}-maximum`}>max</label>
							<input
								id={`${idBase}-maximum`}
								className="dsv-modal__input"
								type="number"
								value={field.maximum ?? ''}
								onChange={(e) => handleChange('maximum', e.target.value)}
								data-testid={`${idBase}-maximum`}
							/>
						</div>
						<div className="dsv-xallof-builder__field-cell">
							<label htmlFor={`${idBase}-exclusive-minimum`}>excl. min</label>
							<input
								id={`${idBase}-exclusive-minimum`}
								className="dsv-modal__input"
								type="number"
								value={field.exclusiveMinimum ?? ''}
								onChange={(e) => handleChange('exclusiveMinimum', e.target.value)}
								aria-describedby={describedBy('exclusiveMinimum')}
								data-testid={`${idBase}-exclusive-minimum`}
							/>
							{renderError('exclusiveMinimum')}
						</div>
						<div className="dsv-xallof-builder__field-cell">
							<label htmlFor={`${idBase}-exclusive-maximum`}>excl. max</label>
							<input
								id={`${idBase}-exclusive-maximum`}
								className="dsv-modal__input"
								type="number"
								value={field.exclusiveMaximum ?? ''}
								onChange={(e) => handleChange('exclusiveMaximum', e.target.value)}
								data-testid={`${idBase}-exclusive-maximum`}
							/>
						</div>
					</>
				)}

				<div className="dsv-xallof-builder__field-cell">
					<label htmlFor={`${idBase}-enum`}>enum</label>
					<input
						id={`${idBase}-enum`}
						className="dsv-modal__input"
						type="text"
						value={Array.isArray(field.enumValues)
							? field.enumValues.join(', ')
							: (field.enumValues ?? '')}
						onChange={(e) => handleChange('enumValues', e.target.value)}
						placeholder="a, b, c"
						data-testid={`${idBase}-enum`}
					/>
				</div>
				<div className="dsv-xallof-builder__field-cell dsv-xallof-builder__field-cell--checkbox">
					<label className="dsv-xallof-builder__inline-label">
						<input
							type="checkbox"
							checked={!!field.nullableInBranch}
							onChange={(e) => handleChange('nullableInBranch', e.target.checked)}
							data-testid={`${idBase}-nullable`}
						/>
						nullable in this branch
					</label>
				</div>
			</div>
		</div>
	);
}

FieldConstraintForm.propTypes = {
	field: PropTypes.object.isRequired,
	branch: PropTypes.oneOf(['then', 'else']).isRequired,
	ruleIndex: PropTypes.number.isRequired,
	errors: PropTypes.object,
	onChange: PropTypes.func.isRequired,
};
